
import { useParams, Navigate } from "react-router-dom";
import { Recipe as RecipeComponent } from "@/components/Recipe";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Database } from "@/integrations/supabase/types";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Loader2 } from "lucide-react";
import NotFound from "./NotFound";
import { Ingredient, Step, CuisineType, FoodType, NutritionalValues } from "@/types/recipe";
import NutritionalInfo from "@/components/recipe/NutritionalInfo";
import { useState, useEffect } from "react";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { SubscriptionCTA } from "@/components/profile/SubscriptionCTA";
import { toast } from "sonner";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

type RecipeRow = Database['public']['Tables']['recipes']['Row'];

const Recipe = () => {
  const { username, id } = useParams();
  const [currentServings, setCurrentServings] = useState<number>(1);
  const [userId, setUserId] = useState<string | null>(null);
  const [subscriptionPrice, setSubscriptionPrice] = useState<number>(3.99);
  const [lifetimePrice, setLifetimePrice] = useState<number>(39.99);

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id || null);
    };
    getUser();
  }, []);

  if (!username || !id || username === ':username' || id === ':id') {
    return <NotFound />;
  }

  const { data: recipe, isLoading } = useQuery({
    queryKey: ["recipe", username, id],
    queryFn: async () => {
      console.log("Fetching recipe:", id, "for normalized username:", username);
      const { data: normalizedData, error: normalizeError } = await supabase
        .rpc('normalize_username', { username });
      
      if (normalizeError) {
        console.error("Error normalizing username:", normalizeError);
        throw normalizeError;
      }

      const { data, error } = await supabase
        .from("recipes")
        .select(`
          *,
          profiles:user_id (
            username,
            avatar_url,
            subscription_price,
            lifetime_subscription_price
          )
        `)
        .eq("id", id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching recipe:", error);
        throw error;
      }
      
      const normalizedProfileUsername = await supabase
        .rpc('normalize_username', { username: data?.profiles.username });
      
      if (!data || normalizedProfileUsername.data !== username) {
        console.log("Recipe not found or username mismatch");
        return null;
      }

      if (data.profiles) {
        setSubscriptionPrice(data.profiles.subscription_price || 5);
        setLifetimePrice(data.profiles.lifetime_subscription_price || 30);
      }
      
      console.log("Recipe data:", data);
      return data as (RecipeRow & { 
        profiles: { 
          username: string; 
          avatar_url: string | null;
          subscription_price: number;
          lifetime_subscription_price: number;
        } 
      });
    },
    enabled: !!username && !!id && username !== ':username' && id !== ':id',
  });

  const { data: stripeConfig } = useQuery({
    queryKey: ['stripe-config'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_secret', {
        secret_name: 'STRIPE_PUBLISHABLE_KEY'
      });
      
      if (error) throw error;
      return { publishableKey: data };
    }
  });

  const { data: subscription } = useSubscriptionStatus(userId, recipe?.user_id);

  const handleSubscribe = async (subscriptionType: 'monthly' | 'lifetime') => {
    if (!userId || !recipe) {
      toast.error("Please log in to subscribe");
      return;
    }

    if (!stripeConfig?.publishableKey) {
      toast.error("Payment system is currently unavailable");
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('handle-payment', {
        body: {
          creatorId: recipe.user_id,
          subscriptionType,
          amount: subscriptionType === 'monthly' ? subscriptionPrice : lifetimePrice
        }
      });

      if (error) throw error;
      return data.clientSecret;
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error("Failed to create subscription");
      return null;
    }
  };

  // Check if current user is the creator
  const isCreator = userId === recipe?.user_id;
  console.log("Recipe access check:", { isCreator, userId, recipeUserId: recipe?.user_id });

  const hasAccess = recipe?.visibility === 'public' || isCreator || !!subscription;
  console.log("Recipe access:", { hasAccess, visibility: recipe?.visibility, isCreator, hasSubscription: !!subscription });
  
  useEffect(() => {
    if (recipe?.servings) {
      setCurrentServings(recipe.servings);
    }
  }, [recipe?.servings]);

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
        <Footer />
      </>
    );
  }

  if (!recipe) {
    return <NotFound />;
  }

  // Show subscription CTA only if the recipe is premium and user doesn't have access
  if (recipe.visibility === 'subscription' && !hasAccess) {
    return (
      <>
        <Header />
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <h1 className="text-2xl font-semibold mb-4">{recipe.title}</h1>
          <p className="text-gray-600 mb-8">This is a premium recipe by {recipe.profiles.username}</p>
          <SubscriptionCTA
            username={recipe.profiles.username}
            creatorId={recipe.user_id}
            subscriptionPrice={subscriptionPrice}
            lifetimeSubscriptionPrice={lifetimePrice}
            onSubscribe={handleSubscribe}
            isSubscribed={!!subscription}
            subscriptionType={subscription?.type}
          />
        </div>
        <Footer />
      </>
    );
  }

  const ingredients = (recipe.ingredients as any[]).map(ing => ({
    ...ing,
    amount: Number(ing.amount)
  })) as Ingredient[];
  
  const steps = (recipe.steps as any[]).map(step => ({
    instruction: step.instruction || '',
    temperature: step.temperature || undefined,
    time: step.time || undefined
  })) as Step[];

  const nutritionalValues = recipe.nutritional_values as unknown as NutritionalValues | null;

  const defaultServings = recipe.servings || 1;

  return (
    <>
      <Header />
      <div className="container max-w-4xl mx-auto px-4 py-8">
        <RecipeComponent
          title={recipe.title}
          description={recipe.description}
          imageUrl={recipe.image_url}
          ingredients={ingredients}
          steps={steps}
          onPrint={() => window.print()}
          author={{
            username: recipe.profiles.username,
            avatarUrl: recipe.profiles.avatar_url
          }}
          recipeId={recipe.id}
          userId={recipe.user_id}
          cuisineType={recipe.cuisine_type as CuisineType}
          foodType={recipe.food_type as FoodType}
          defaultServings={defaultServings}
          onServingsChange={setCurrentServings}
          nutritionalValues={nutritionalValues}
        />
      </div>
      <Footer />
    </>
  );
};

export default Recipe;
